import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download,
  Eye,
  Copy,
  Check,
  Grid,
  X,
  Image as ImageIcon,
  FileText,
  Palette,
  Loader2
} from 'lucide-react';
import { mediaAssets, MediaAsset, AssetVariant } from './MediaAssets';

interface MediaGalleryProps {
  onDownloadAll: () => void;
  isDownloading?: boolean;
}

const MediaGallery: React.FC<MediaGalleryProps> = ({ onDownloadAll, isDownloading = false }) => {
  const { isDarkMode } = useTheme();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [previewAsset, setPreviewAsset] = useState<{asset: MediaAsset, variant: AssetVariant} | null>(null);
  const [copiedVariant, setCopiedVariant] = useState<string | null>(null);
  const [downloadingVariant, setDownloadingVariant] = useState<string | null>(null);

  const categories = [
    { value: 'all', label: 'All Assets', icon: Grid },
    { value: 'logo', label: 'Logos', icon: Palette },
    { value: 'icon', label: 'Icons', icon: ImageIcon },
    { value: 'feature', label: 'Features', icon: ImageIcon },
    { value: 'brand', label: 'Brand Elements', icon: FileText }
  ];

  const filteredAssets = selectedCategory === 'all' 
    ? mediaAssets 
    : mediaAssets.filter(asset => asset.category === selectedCategory);

  const handleCopyUrl = async (variant: AssetVariant) => {
    const fullUrl = `${window.location.origin}${variant.downloadPath}`;
    await navigator.clipboard.writeText(fullUrl);
    setCopiedVariant(variant.id);
    setTimeout(() => setCopiedVariant(null), 2000);
  };

  const handleDownload = async (variant: AssetVariant) => {
    try {
      setDownloadingVariant(variant.id);
      
      // Fetch the file to ensure it exists and get the blob
      const response = await fetch(variant.downloadPath);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${variant.downloadPath}`);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Create download link with proper filename
      const link = document.createElement('a');
      link.href = url;
      link.download = `${variant.name.replace(/[^a-zA-Z0-9]/g, '_')}.${variant.format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback to direct link if fetch fails
      window.open(variant.downloadPath, '_blank');
    } finally {
      setDownloadingVariant(null);
    }
  };

  const getBackgroundStyle = (background: string) => {
    switch (background) {
      case 'transparent':
        return 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800';
      case 'white':
        return 'bg-white';
      case 'dark':
        return 'bg-gray-900';
      case 'color':
        return 'bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20';
      default:
        return 'bg-gray-50 dark:bg-gray-800';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className={`text-3xl font-bold mb-4 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          Media Kit Assets
        </h2>
        <p className={`text-lg mb-6 ${
          isDarkMode ? 'text-gray-300' : 'text-gray-600'
        }`}>
          Download high-quality Newsroom brand assets in multiple formats
        </p>
        <button
          onClick={onDownloadAll}
          disabled={isDownloading}
          className="inline-flex items-center px-6 py-3 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
        >
          {isDownloading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Generating Press Kit...
            </>
          ) : (
            <>
              <Download className="w-5 h-5 mr-2" />
              Download Complete Media Kit
            </>
          )}
        </button>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-4">
        {categories.map((category) => {
          const Icon = category.icon;
          return (
            <button
              key={category.value}
              onClick={() => setSelectedCategory(category.value)}
              className={`inline-flex items-center px-4 py-2 rounded-lg transition-all ${
                selectedCategory === category.value
                  ? 'bg-blue-600 text-white shadow-md'
                  : isDarkMode
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {category.label}
            </button>
          );
        })}
      </div>

      {/* Assets Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {filteredAssets.map((asset) => (
          <motion.div
            key={asset.id}
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={`rounded-2xl border ${
              isDarkMode 
                ? 'bg-gray-800/70 border-gray-700/50' 
                : 'bg-white border-gray-200'
            } overflow-hidden`}
          >
            {/* Asset Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className={`text-xl font-semibold mb-2 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {asset.name}
              </h3>
              <p className={`text-sm mb-3 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {asset.description}
              </p>
              <div className="flex flex-wrap gap-2">
                {asset.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`px-2 py-1 text-xs rounded-full ${
                      isDarkMode
                        ? 'bg-gray-700 text-gray-300'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Asset Variants */}
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {asset.variants.map((variant) => (
                  <div
                    key={variant.id}
                    className={`border rounded-xl overflow-hidden ${
                      isDarkMode ? 'border-gray-700' : 'border-gray-200'
                    }`}
                  >
                    {/* Preview Area */}
                    <div 
                      className={`aspect-video ${getBackgroundStyle(variant.background || 'transparent')} p-4 flex items-center justify-center relative`}
                    >
                      <div className="relative group">
                        <img
                          src={variant.path}
                          alt={variant.name}
                          className="max-w-full max-h-full object-contain cursor-pointer hover:scale-105 transition-transform"
                          onClick={() => setPreviewAsset({asset, variant})}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer">
                          <Eye className="w-6 h-6 text-white" />
                        </div>
                      </div>
                    </div>

                    {/* Variant Info */}
                    <div className={`p-3 ${
                      isDarkMode ? 'bg-gray-900/50' : 'bg-gray-50'
                    }`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className={`font-medium text-sm ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {variant.name}
                          </h4>
                          <p className={`text-xs ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {variant.format.toUpperCase()} • {variant.size}
                          </p>
                          {variant.dimensions && (
                            <p className={`text-xs ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              {variant.dimensions}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDownload(variant)}
                          disabled={downloadingVariant === variant.id}
                          className={`flex-1 inline-flex items-center justify-center px-3 py-2 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            isDarkMode
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {downloadingVariant === variant.id ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Download className="w-3 h-3 mr-1" />
                          )}
                          {downloadingVariant === variant.id ? 'Downloading...' : 'Download'}
                        </button>
                        <button
                          onClick={() => handleCopyUrl(variant)}
                          className={`px-3 py-2 rounded-lg text-xs transition-colors ${
                            copiedVariant === variant.id
                              ? 'bg-green-600 text-white'
                              : isDarkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {copiedVariant === variant.id ? (
                            <Check className="w-3 h-3" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Usage Guidelines */}
              <div className={`mt-4 p-3 rounded-lg ${
                isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'
              }`}>
                <p className={`text-xs ${
                  isDarkMode ? 'text-blue-300' : 'text-blue-800'
                }`}>
                  <strong>Usage:</strong> {asset.usage}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Preview Modal */}
      <AnimatePresence>
        {previewAsset && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className={`max-w-4xl w-full max-h-[90vh] rounded-2xl overflow-hidden ${
                isDarkMode ? 'bg-gray-800' : 'bg-white'
              }`}
            >
              {/* Modal Header */}
              <div className={`p-4 border-b flex justify-between items-center ${
                isDarkMode ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <div>
                  <h3 className={`font-semibold ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {previewAsset.asset.name} - {previewAsset.variant.name}
                  </h3>
                  <p className={`text-sm ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {previewAsset.variant.format.toUpperCase()} • {previewAsset.variant.size} • {previewAsset.variant.dimensions}
                  </p>
                </div>
                <button
                  onClick={() => setPreviewAsset(null)}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode
                      ? 'hover:bg-gray-700 text-gray-400'
                      : 'hover:bg-gray-100 text-gray-500'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-8">
                <div className={`rounded-xl ${getBackgroundStyle(previewAsset.variant.background || 'transparent')} p-8 flex items-center justify-center`}>
                  <img
                    src={previewAsset.variant.path}
                    alt={previewAsset.variant.name}
                    className="max-w-full max-h-96 object-contain"
                  />
                </div>

                {/* Modal Actions */}
                <div className="flex justify-center gap-4 mt-6">
                  <button
                    onClick={() => handleDownload(previewAsset.variant)}
                    disabled={downloadingVariant === previewAsset.variant.id}
                    className="inline-flex items-center px-6 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {downloadingVariant === previewAsset.variant.id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    {downloadingVariant === previewAsset.variant.id ? 'Downloading...' : 'Download'}
                  </button>
                  <button
                    onClick={() => handleCopyUrl(previewAsset.variant)}
                    className={`inline-flex items-center px-6 py-3 rounded-lg border transition-colors ${
                      isDarkMode
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy URL
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MediaGallery;
